import React, { useEffect } from "react";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { OrganizationWithUsers } from "@/lib/types";
import { UserList } from "@/components/UserList";
import { api } from "@/lib/api";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const AddUserFormSchema = z.object({
  organization_id: z.string().min(1, { message: "Select an organization" }),
  email: z.string().email({ message: "Invalid email address" }),
  name: z.string().optional(),
  role: z.enum(["admin", "user", "staff"]),
  is_superuser: z.boolean(),
});

function OrganizationDetails() {
  const { organizations, selectedOrganizationId, updateOrganization } =
    useOrganizationsStore();
  const [organization, setOrganization] =
    React.useState<OrganizationWithUsers | null>(null);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = React.useState(false);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof AddUserFormSchema>>({
    resolver: zodResolver(AddUserFormSchema),
    defaultValues: {
      organization_id: selectedOrganizationId ?? "",
      email: "",
      name: "",
      role: "user",
      is_superuser: false,
    },
  });

  // Default the invite target to the currently-selected org once it loads.
  useEffect(() => {
    if (selectedOrganizationId && !form.getValues("organization_id")) {
      form.setValue("organization_id", selectedOrganizationId);
    }
  }, [selectedOrganizationId, form]);

  useEffect(() => {
    if (selectedOrganizationId) {
      const org = organizations.find((o) => o.id === selectedOrganizationId);
      if (org) {
        setOrganization(org);
      } else {
        api
          .getOrganization(selectedOrganizationId)
          .then((orgData) => {
            setOrganization(orgData);
            updateOrganization(selectedOrganizationId, orgData);
          })
          .catch(console.error);
      }
    }
  }, [selectedOrganizationId, organizations, updateOrganization]);

  const onSubmit = async (data: z.infer<typeof AddUserFormSchema>) => {
    const targetOrgId = data.organization_id;
    if (!targetOrgId) return;
    const targetOrgName =
      organizations.find((o) => o.id === targetOrgId)?.name ?? "the organization";
    try {
      const updatedOrg = await api.inviteUserToOrganization(targetOrgId, {
        email: data.email,
        name: data.name?.trim() ? data.name.trim() : undefined,
        role: data.role,
        is_superuser: data.is_superuser,
      });
      updateOrganization(targetOrgId, updatedOrg);
      // Only refresh the on-screen card if we invited into the org being viewed.
      if (targetOrgId === selectedOrganizationId) {
        setOrganization(updatedOrg);
      }
      toast({
        title: "User invited",
        description: `${data.email} was sent a Cognito invite and added to ${targetOrgName}.`,
      });
      form.reset({
        organization_id: targetOrgId,
        email: "",
        name: "",
        role: "user",
        is_superuser: false,
      });
      setIsAddUserDialogOpen(false);
    } catch (error) {
      console.error("Failed to invite user:", error);
      toast({
        title: "Failed to invite user",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  if (!organization) {
    return <div>Loading...</div>;
  }

  return (
    <div className="m-4 md:m-8">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h1 className="text-2xl font-bold break-words">{organization.name}</h1>
      </div>
      <Card className="w-full">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
          <CardTitle>Users</CardTitle>
          <Dialog
            open={isAddUserDialogOpen}
            onOpenChange={setIsAddUserDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>Invite User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite User</DialogTitle>
                <DialogDescription>
                  Creates the user's account, emails them an invite, and adds
                  them to {organization.name}.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormItem>
                    <FormLabel>Organization</FormLabel>
                    <Select
                      value={form.watch("organization_id")}
                      onValueChange={(value) =>
                        form.setValue("organization_id", value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an organization" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {organizations.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage>
                      {form.formState.errors.organization_id?.message}
                    </FormMessage>
                  </FormItem>
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="user@example.com"
                        {...form.register("email")}
                      />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.email?.message}
                    </FormMessage>
                  </FormItem>
                  <FormItem>
                    <FormLabel>Name (optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Jane Doe"
                        {...form.register("name")}
                      />
                    </FormControl>
                  </FormItem>
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select
                      value={form.watch("role")}
                      onValueChange={(value) =>
                        form.setValue(
                          "role",
                          value as "admin" | "user" | "staff"
                        )
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={form.watch("is_superuser")}
                        onCheckedChange={(checked) =>
                          form.setValue("is_superuser", checked === true)
                        }
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Grant superuser (global admin) access
                    </FormLabel>
                  </FormItem>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting
                        ? "Inviting…"
                        : "Send invite"}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <UserList users={organization.users.map((ou) => ou.user)} />
        </CardContent>
      </Card>
    </div>
  );
}

export default OrganizationDetails;
